// gameLocalStorage.ts
// Utility for managing game state in localStorage

import { useDebug } from "@/utils/useDebugLog";
import { DEBUG_LOGS } from './constants';
const { log } = useDebug({ name: 'GameLocalStorage', bgColor: 'orange', enabled: DEBUG_LOGS });

const STORAGE_KEY = 'coverStoryGameData';

// Completed game data (saved when user wins or loses)
export interface GameProgress {
  gameNumber: string;
  emojiSet: string;
  completedAt: string; // ISO timestamp
  guessCount: number;
  won: boolean;
  tilesRevealed: number; // Number of tiles that were revealed
  gameTitle: string; // Title of the game (e.g., "Born in the U.S.A.")
  guessCountFormatted: string; // Formatted string like "(7/10)"
}

// In-progress game data (saved during gameplay, cleared when game completes)
export interface GameInProgress {
  gameNumber: string;
  gameTitle: string;
  gridSize: number; // e.g., 6 for a 6x6 grid
  flippedTileIds: number[]; // Array of tile IDs that have been flipped
  guessMeterData: Array<'tile' | 'incorrect'>; // Meter state (each entry is a guess)
  guessesUsed: number; // Number of guesses used (should match guessMeterData.length)
  tilesRevealed: number; // Number of board tiles revealed
  lastUpdated: string; // ISO timestamp of last save
}

// Overall structure stored in localStorage
export interface GameLocalStorageData {
  hasSeenInfoModal: boolean;
  completedGames: GameProgress[];
  inProgressGames: GameInProgress[]; // New: track games in progress
}

// Get all game data from localStorage
export const getGameData = (): GameLocalStorageData => {
  if (typeof window === 'undefined') {
    return { hasSeenInfoModal: false, completedGames: [], inProgressGames: [] };
  }

  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) {
      log('getGameData', 'No data found in localStorage');
      return { hasSeenInfoModal: false, completedGames: [], inProgressGames: [] };
    }
    const parsed = JSON.parse(data);

    // Migrate old data structure if needed (add inProgressGames if missing)
    if (!parsed.inProgressGames) {
      parsed.inProgressGames = [];
    }

    log('getGameData', 'Data loaded', parsed);
    return parsed;
  } catch (error) {
    log('getGameData', 'Error reading data', error);
    return { hasSeenInfoModal: false, completedGames: [], inProgressGames: [] };
  }
};

// Save game data to localStorage
export const saveGameData = (data: GameLocalStorageData): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    log('saveGameData', 'Data saved', { completedGames: data.completedGames.length, hasSeenInfoModal: data.hasSeenInfoModal });
  } catch (error) {
    log('saveGameData', 'Error saving data', error);
  }
};

// Check if user has seen the info modal
export const hasSeenInfoModal = (): boolean => {
  const data = getGameData();
  return data.hasSeenInfoModal;
};

// Mark that user has seen the info modal
export const setHasSeenInfoModal = (seen: boolean = true): void => {
  const data = getGameData();
  data.hasSeenInfoModal = seen;
  saveGameData(data);
  log('setHasSeenInfoModal', 'Set hasSeenInfoModal', seen);
};

// Check if a specific game has been completed
export const hasCompletedGame = (gameNumber: string): boolean => {
  const data = getGameData();
  return data.completedGames.some(game => game.gameNumber === gameNumber);
};

// Get completed game data for a specific game
export const getCompletedGame = (gameNumber: string): GameProgress | null => {
  const data = getGameData();
  return data.completedGames.find(game => game.gameNumber === gameNumber) || null;
};

// Save a completed game
export const saveCompletedGame = (progress: GameProgress): void => {
  const data = getGameData();

  // Remove existing entry for this game if it exists
  data.completedGames = data.completedGames.filter(
    game => game.gameNumber !== progress.gameNumber
  );

  // Add new entry
  data.completedGames.push(progress);

  // Also clear any in-progress data for this game since it's now complete
  data.inProgressGames = data.inProgressGames.filter(
    game => game.gameNumber !== progress.gameNumber
  );

  saveGameData(data);
  log('saveCompletedGame', 'Saved completed game', { gameNumber: progress.gameNumber, won: progress.won, guessCount: progress.guessCount });
};

// Get all completed games
export const getAllCompletedGames = (): GameProgress[] => {
  const data = getGameData();
  return data.completedGames;
};

// Get in-progress game data for a specific game
export const getInProgressGame = (gameNumber: string): GameInProgress | null => {
  const data = getGameData();
  return data.inProgressGames.find(game => game.gameNumber === gameNumber) || null;
};

// Save or update in-progress game
export const saveInProgressGame = (progress: GameInProgress): void => {
  const data = getGameData();

  // Remove existing entry for this game if it exists
  data.inProgressGames = data.inProgressGames.filter(
    game => game.gameNumber !== progress.gameNumber
  );

  // Add new entry with updated timestamp
  progress.lastUpdated = new Date().toISOString();
  data.inProgressGames.push(progress);

  saveGameData(data);
  log('saveInProgressGame', 'Saved in-progress game', {
    gameNumber: progress.gameNumber,
    flippedTiles: progress.flippedTileIds.length,
    guesses: progress.guessesUsed
  });
};

// Clear in-progress game (called when game is completed or user wants to restart)
export const clearInProgressGame = (gameNumber: string): void => {
  const data = getGameData();

  const beforeCount = data.inProgressGames.length;
  data.inProgressGames = data.inProgressGames.filter(
    game => game.gameNumber !== gameNumber
  );
  const afterCount = data.inProgressGames.length;

  if (beforeCount !== afterCount) {
    saveGameData(data);
    log('clearInProgressGame', 'Cleared in-progress game', { gameNumber });
  }
};

// Clear all game data (for testing/debugging)
export const clearGameData = (): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(STORAGE_KEY);
    log('clearGameData', 'All data cleared');
  } catch (error) {
    log('clearGameData', 'Error clearing data', error);
  }
};

/* ============================================
 * USAGE EXAMPLES
 * ============================================
 *
 * // Info Modal
 * // ----------
 * const seen = hasSeenInfoModal();
 * setHasSeenInfoModal(true);
 *
 * // Completed Games
 * // ---------------
 * // Check if a specific game is completed
 * if (hasCompletedGame('42')) {
 *   const gameData = getCompletedGame('42');
 *   console.log(gameData?.emojiSet);
 * }
 *
 * // Save a completed game
 * saveCompletedGame({
 *   gameNumber: '42',
 *   emojiSet: '🟩🟩🟩⬜⬜⬜⬜⬜⬜⬜',
 *   completedAt: new Date().toISOString(),
 *   guessCount: 3,
 *   won: true,
 *   tilesRevealed: 12,
 *   gameTitle: 'Born in the U.S.A.',
 *   guessCountFormatted: '(3/10)',
 * });
 *
 * // Get user stats
 * const allGames = getAllCompletedGames();
 * const wonGames = allGames.filter(g => g.won).length;
 * const winRate = Math.round((wonGames / allGames.length) * 100);
 *
 * // In-Progress Games
 * // -----------------
 * // Check for in-progress game on mount
 * const inProgressData = getInProgressGame('42');
 * if (inProgressData) {
 *   console.log('Restoring game:', inProgressData.flippedTileIds);
 * }
 *
 * // Save progress after each guess/tile flip
 * saveInProgressGame({
 *   gameNumber: '42',
 *   gameTitle: 'Born in the U.S.A.',
 *   gridSize: 6,
 *   flippedTileIds: [0, 1, 5, 10, 15],
 *   guessMeterData: ['tile', 'incorrect', 'tile'],
 *   guessesUsed: 3,
 *   tilesRevealed: 5,
 *   lastUpdated: new Date().toISOString(), // Auto-set by saveInProgressGame
 * });
 *
 * // Clear in-progress data (when game completes or user restarts)
 * clearInProgressGame('42');
 *
 * // Clear all data (testing only)
 * clearGameData();
 *
 * // Storage Size Considerations
 * // ---------------------------
 * // Each in-progress game saves:
 * // - flippedTileIds: ~50-150 bytes (depends on grid size, max 144 tiles for 12x12)
 * // - guessMeterData: ~20-50 bytes (max 10 guesses)
 * // - Other metadata: ~100 bytes
 * // Total per game: ~200-300 bytes
 * // LocalStorage limit: ~5-10MB (browser dependent)
 * // This allows for hundreds of in-progress games before hitting limits
 */
